/**
 * Settings for the open workspace (profile, account, workspace, members): the platform services the settings view
 * models need, provided by the app shell next to WorkspaceContext — only where the feature is on. Absent → no settings
 * screens, no member cards, no role badges. Native: an `@Environment` value / `CompositionLocal`.
 *
 * `MemberCardContext` opens a member's profile card (photo, title, local time, status, "Message") from anywhere a
 * name or avatar is shown.
 */
import {
  AccountSecurityViewModel,
  type AccountSecurity,
  type AccountSecurityState,
  AccountSettingsViewModel,
  DataSettingsViewModel,
  DriveImportViewModel,
  type DriveImportState,
  ImportSettingsViewModel,
  SecuritySettingsViewModel,
  AppPasswordsViewModel,
  type AppPasswordsState,
  type DataSettingsState,
  type ImportSettingsState,
  type SecuritySettingsState,
  MembersViewModel,
  NavigationViewModel,
  type NavigationState,
  type KeyValueStorage,
  type ActivityViewModel,
  type DndViewModel,
  ProfileSettingsViewModel,
  WorkspaceSettingsViewModel,
  type AccountSettingsState,
  type MembersState,
  type NotificationsViewModel,
  type Notify,
  type ProfileSettingsState,
  type SettingsDeps,
  type WorkspaceSettingsState,
} from "@bundle/core";
import type { Locale } from "@bundle/i18n";
import { createContext, useCallback, useContext, useSyncExternalStore } from "react";
import { useWorkspaceContext } from "./AppContext.tsx";
import { useOwned } from "../hooks/owned.ts";

/** This device's notification sound setting and preview (web/desktop: apps/web platform/notificationSound.ts). */
export type NotificationSoundControl = {
  subscribe: (fn: () => void) => () => void;
  getSnapshot: () => Notify.SoundSetting;
  set: (setting: Notify.SoundSetting) => void;
  preview: (sound: Notify.NotificationSound) => void;
};

export type SettingsContextValue = {
  deps: SettingsDeps;
  /** This device's push notifications (absent where the platform has none to offer). */
  notifications?: NotificationsViewModel;
  /** This device's notification sound (absent where the platform plays none). */
  sound?: NotificationSoundControl;
  /** Copies text to the clipboard (profile and invite links). */
  copy: (text: string) => Promise<void>;
  /** The app's web origin, for shareable links ("https://app.bundle.direct"). */
  origin: string;
  /** The app's current language (profile settings start from it). */
  locale: () => Locale;
  /** My Activity feed and its unread badge (one per workspace). */
  activity?: ActivityViewModel;
  /** Do not disturb: my server-side pause and schedule (Settings → Notifications, the moon on my avatar). */
  dnd?: DndViewModel;
  /** Account & sign-in: my sign-in emails, password, passkeys and two-step verification (absent: the page shows only the email). */
  accountSecurity?: AccountSecurity;
  /** Saves a text file (backup codes; web: a download). */
  saveTextFile?: (name: string, text: string) => void;
  /** "Use the number from my Google account" can ask Google for the phone scope here (web: yes; desktop: no). */
  googlePhone?: boolean;
  /** Back from Google with the phone scope: true once (then the phone sheet opens with the number read from Google). */
  takePhoneImport?: () => boolean;
};

export const SettingsContext = createContext<SettingsContextValue | null>(null);

/** Settings services, or null where settings are off. */
export function useSettingsContext(): SettingsContextValue | null {
  return useContext(SettingsContext);
}

export type MemberCard = { open: (userId: string) => void };

export const MemberCardContext = createContext<MemberCard | null>(null);

/** Opens member profile cards (null where settings are off: names and avatars aren't pressable then). */
export function useMemberCard(): MemberCard | null {
  return useContext(MemberCardContext);
}

export type GroupCard = { open: (groupId: string) => void };

export const GroupCardContext = createContext<GroupCard | null>(null);

/** Opens user group cards (clicking a group mention); null where settings are off. */
export function useGroupCard(): GroupCard | null {
  return useContext(GroupCardContext);
}

function required(ctx: SettingsContextValue | null): SettingsContextValue {
  if (ctx === null) throw new Error("SettingsContext provider missing");
  return ctx;
}

function useVm<T extends { subscribe: (l: () => void) => () => void; getSnapshot: () => S; dispose: () => void }, S>(key: string, create: () => T): { vm: T; state: S } {
  const vm = useOwned(key, create);
  return { vm, state: useSyncExternalStore(vm.subscribe, vm.getSnapshot, vm.getSnapshot) };
}

/** My profile in this workspace (one per workspace; `load()` when settings open). */
export function useProfileSettings(): { vm: ProfileSettingsViewModel; state: ProfileSettingsState } {
  const { workspace } = useWorkspaceContext();
  const ctx = required(useSettingsContext());
  const create = useCallback(() => new ProfileSettingsViewModel(workspace, ctx.deps, ctx.locale), [workspace, ctx]);
  return useVm(`settings:profile:${workspace.wsId}:${workspace.state.meId}`, create);
}

export function useAccountSettings(): { vm: AccountSettingsViewModel; state: AccountSettingsState } {
  const { workspace } = useWorkspaceContext();
  const ctx = required(useSettingsContext());
  const create = useCallback(() => new AccountSettingsViewModel(ctx.deps), [ctx]);
  return useVm(`settings:account:${workspace.state.meId}`, create);
}

/** Account & sign-in: emails, password, passkeys, two-step (null where the platform has no AccountSecurity). */
export function useAccountSecurity(): { vm: AccountSecurityViewModel; state: AccountSecurityState } | null {
  const { workspace } = useWorkspaceContext();
  const ctx = required(useSettingsContext());
  const security = ctx.accountSecurity;
  const create = useCallback(
    () => new AccountSecurityViewModel({ security: security ?? NO_SECURITY, account: () => ctx.deps.api.account(), locale: ctx.locale() }),
    [ctx, security],
  );
  const owned = useVm<AccountSecurityViewModel, AccountSecurityState>(`settings:accountSecurity:${workspace.state.meId}`, create);
  return security === undefined ? null : owned;
}

const unavailable = () => Promise.reject(new Error("Account & sign-in is not available here"));
/** Stands in where the platform offers no AccountSecurity (the hook then returns null). */
const NO_SECURITY: AccountSecurity = new Proxy({} as AccountSecurity, { get: () => unavailable });

/** Settings → Security: my sessions (+ the workspace summary for owners and admins). */
export function useSecuritySettings(): { vm: SecuritySettingsViewModel; state: SecuritySettingsState } {
  const { workspace } = useWorkspaceContext();
  const ctx = required(useSettingsContext());
  const create = useCallback(() => new SecuritySettingsViewModel(workspace, ctx.deps), [workspace, ctx]);
  return useVm(`settings:security:${workspace.wsId}:${workspace.state.meId}`, create);
}

/** Settings → Security → App passwords: mine in this workspace. */
export function useAppPasswords(): { vm: AppPasswordsViewModel; state: AppPasswordsState } {
  const { workspace } = useWorkspaceContext();
  const ctx = required(useSettingsContext());
  const create = useCallback(() => new AppPasswordsViewModel(workspace, ctx.deps), [workspace, ctx]);
  return useVm(`settings:appPasswords:${workspace.wsId}:${workspace.state.meId}`, create);
}

/**
 * Settings → Navigation: my phone tabs, saved with my account. The web has no bottom bar to draw on its first frame,
 * so it keeps no device copy.
 */
export function useNavigationSettings(): { vm: NavigationViewModel; state: NavigationState } {
  const { workspace } = useWorkspaceContext();
  const ctx = required(useSettingsContext());
  const create = useCallback(() => new NavigationViewModel({ api: ctx.deps.api, storage: memoryStorage(), userId: workspace.state.meId }), [workspace, ctx]);
  return useVm(`settings:navigation:${workspace.state.meId}`, create);
}

function memoryStorage(): KeyValueStorage {
  const m = new Map<string, string>();
  return { get: (k) => m.get(k), set: (k, v) => void m.set(k, v), remove: (k) => void m.delete(k) };
}

/** Settings → Data: my export (+ the workspace export for owners and admins). */
export function useDataSettings(): { vm: DataSettingsViewModel; state: DataSettingsState } {
  const { workspace } = useWorkspaceContext();
  const ctx = required(useSettingsContext());
  const create = useCallback(() => new DataSettingsViewModel(workspace, ctx.deps), [workspace, ctx]);
  return useVm(`settings:data:${workspace.wsId}:${workspace.state.meId}`, create);
}

/** Settings → Import: Slack and Notion imports (owners and admins). */
export function useImportSettings(): { vm: ImportSettingsViewModel; state: ImportSettingsState } {
  const { workspace } = useWorkspaceContext();
  const ctx = required(useSettingsContext());
  const create = useCallback(() => new ImportSettingsViewModel(workspace, ctx.deps), [workspace, ctx]);
  return useVm(`settings:import:${workspace.wsId}:${workspace.state.meId}`, create);
}

/** Settings → Import → Google Drive (owners and admins). */
export function useDriveImport(): { vm: DriveImportViewModel; state: DriveImportState } {
  const { workspace } = useWorkspaceContext();
  const ctx = required(useSettingsContext());
  const create = useCallback(() => new DriveImportViewModel(workspace, ctx.deps), [workspace, ctx]);
  return useVm(`settings:drive-import:${workspace.wsId}:${workspace.state.meId}`, create);
}

/** Settings → Data: my own Slack import (any active person, not guests). */
export function usePersonalImport(): { vm: ImportSettingsViewModel; state: ImportSettingsState } {
  const { workspace } = useWorkspaceContext();
  const ctx = required(useSettingsContext());
  const create = useCallback(() => new ImportSettingsViewModel(workspace, ctx.deps, { personal: true }), [workspace, ctx]);
  return useVm(`settings:personal-import:${workspace.wsId}:${workspace.state.meId}`, create);
}

export function useWorkspaceSettings(): { vm: WorkspaceSettingsViewModel; state: WorkspaceSettingsState } {
  const { workspace } = useWorkspaceContext();
  const ctx = required(useSettingsContext());
  const create = useCallback(() => new WorkspaceSettingsViewModel(workspace, ctx.deps), [workspace, ctx]);
  return useVm(`settings:workspace:${workspace.wsId}:${workspace.state.meId}`, create);
}

/** Members, invitations and guests & agents share one view model per workspace. */
export function useMembers(): { vm: MembersViewModel; state: MembersState } {
  const { workspace } = useWorkspaceContext();
  const ctx = required(useSettingsContext());
  const create = useCallback(() => new MembersViewModel(workspace, ctx.deps), [workspace, ctx]);
  return useVm(`settings:members:${workspace.wsId}:${workspace.state.meId}`, create);
}
